// ═══════════════════════════════════════════════════════════════════════════
// ConnectIcons — inline SVG glyphs used across the Connect page.
// Kept local (and dependency-free) so the page renders without any asset or
// network fetch. All icons inherit `currentColor`.
// ═══════════════════════════════════════════════════════════════════════════

const base = (size) => ({
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.7,
  strokeLinecap: "round",
  strokeLinejoin: "round",
});

export const RouteIcon = ({ size = 18 }) => (
  <svg {...base(size)}>
    <circle cx="6" cy="5" r="2.2" />
    <circle cx="6" cy="19" r="2.2" />
    <circle cx="18" cy="12" r="2.2" />
    <path d="M6 7.2v9.6" />
    <path d="M8.2 5H13a3 3 0 0 1 3 3v1.8" />
  </svg>
);

export const DesktopIcon = ({ size = 18 }) => (
  <svg {...base(size)}>
    <rect x="2.5" y="4" width="19" height="12.5" rx="1.5" />
    <path d="M9 20.5h6M12 16.5v4" />
  </svg>
);

export const MobileIcon = ({ size = 18 }) => (
  <svg {...base(size)}>
    <rect x="7" y="2.5" width="10" height="19" rx="2" />
    <path d="M11 18.5h2" />
  </svg>
);

export const CheckIcon = ({ size = 16 }) => (
  <svg {...base(size)}>
    <path d="M4.5 12.5l5 5 10-11" />
  </svg>
);

export const DownloadIcon = ({ size = 16 }) => (
  <svg {...base(size)}>
    <path d="M12 3.5v11" />
    <path d="M7.5 10.5L12 15l4.5-4.5" />
    <path d="M4.5 19.5h15" />
  </svg>
);

export const ShieldIcon = ({ size = 18 }) => (
  <svg {...base(size)}>
    <path d="M12 2.8l7.5 3v6c0 4.4-3.1 8.2-7.5 9.4-4.4-1.2-7.5-5-7.5-9.4v-6z" />
  </svg>
);

// ── Platform marks (filled, brand-shaped) ────────────────────────────────────
export const WindowsMark = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M3 5.4l7.6-1.05v7.2H3zM11.5 4.2L21 2.9v8.65h-9.5zM3 12.55h7.6v7.15L3 18.65zM11.5 12.55H21v8.6l-9.5-1.3z" />
  </svg>
);

export const AppleMark = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M16.2 12.5c0-2.15 1.75-3.18 1.83-3.23-1-1.46-2.55-1.66-3.1-1.68-1.32-.13-2.58.78-3.25.78-.67 0-1.7-.76-2.8-.74-1.44.02-2.77.84-3.51 2.13-1.5 2.6-.38 6.45 1.08 8.56.71 1.03 1.56 2.19 2.68 2.15 1.08-.04 1.49-.7 2.79-.7 1.3 0 1.67.7 2.8.68 1.16-.02 1.89-1.05 2.6-2.09.82-1.2 1.16-2.36 1.18-2.42-.03-.01-2.26-.87-2.3-3.44zM14.1 5.9c.59-.72.99-1.72.88-2.72-.85.04-1.88.57-2.49 1.28-.55.63-1.03 1.65-.9 2.62.95.08 1.92-.48 2.51-1.18z" />
  </svg>
);

export const LinuxMark = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2c-2.3 0-3.6 1.7-3.6 4.2 0 1.5.2 2.3-.4 3.3-.9 1.5-2.4 3.2-2.9 5.3-.4 1.6.2 2.3 1 2.4.3 1.3 1.6 2.4 3.1 2.7 1.7.4 3.9.3 5.4-.4 1-.5 1.6-1.3 1.8-2.3.9-.2 1.4-.9 1-2.4-.5-2.1-2-3.8-2.9-5.3-.6-1-.4-1.8-.4-3.3C15.1 3.7 14.3 2 12 2zm-1.5 3.1c.4 0 .7.4.7 1s-.3 1-.7 1-.7-.4-.7-1 .3-1 .7-1zm3 0c.4 0 .7.4.7 1s-.3 1-.7 1-.7-.4-.7-1 .3-1 .7-1zM12 7.6c.8 0 1.7.5 1.7.9 0 .4-.9 1-1.7 1s-1.7-.6-1.7-1c0-.4.9-.9 1.7-.9z" />
  </svg>
);

export const AndroidMark = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.53 9.32l1.79-3.1a.37.37 0 1 0-.64-.37l-1.81 3.14A11.1 11.1 0 0 0 12 8.05c-1.7 0-3.3.33-4.87.94L5.32 5.85a.37.37 0 0 0-.64.37l1.79 3.1A6.44 6.44 0 0 0 3.1 14.5h17.8a6.44 6.44 0 0 0-3.37-5.18zM8.02 12.6a.79.79 0 1 1 0-1.58.79.79 0 0 1 0 1.58zm7.96 0a.79.79 0 1 1 0-1.58.79.79 0 0 1 0 1.58z" />
  </svg>
);

export const PlayMark = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <path d="M4 3.2v17.6c0 .6.65.95 1.15.63l3.6-2.4-4.75-16z" fill="#34A853" />
    <path d="M4 3.2l4.75 16 2.6-1.73L4 3.2z" fill="#4285F4" />
    <path d="M8.75 19.03l-2.6 1.73 8.9-5.93-2.35-2.35-3.95 6.55z" fill="#FBBC04" />
    <path d="M12.7 12.48l2.35 2.35 4.35-2.9c.6-.4.6-1.26 0-1.66l-4.35-2.9-2.35 5.11z" fill="#EA4335" />
  </svg>
);

// ── The little QR-ish block used in the mobile tab (visual placeholder) ──────
export const QrPlaceholder = ({ size = 260 }) => {
  const N = 25;                       // modules per side
  const cell = 100 / N;
  const finder = (x, y) => (
    <g key={`f${x}${y}`}>
      <rect x={x * cell} y={y * cell} width={cell * 7} height={cell * 7} fill="#000" />
      <rect x={(x + 1) * cell} y={(y + 1) * cell} width={cell * 5} height={cell * 5} fill="#fff" />
      <rect x={(x + 2) * cell} y={(y + 2) * cell} width={cell * 3} height={cell * 3} fill="#000" />
    </g>
  );
  // Deterministic pseudo-random fill so the block looks like a real QR code
  // without pulling in a generator. Replace with a real code when wiring data.
  const inFinder = (c, r) =>
    (c < 8 && r < 8) || (c > N - 9 && r < 8) || (c < 8 && r > N - 9);
  const cells = [];
  for (let r = 0; r < N; r++) {
    for (let c = 0; c < N; c++) {
      if (inFinder(c, r)) continue;
      if (((c * 7 + r * 13 + ((c * r) % 5)) % 3) === 0) {
        cells.push(<rect key={`${c}-${r}`} x={c * cell} y={r * cell} width={cell} height={cell} fill="#000" />);
      }
    }
  }
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" shapeRendering="crispEdges" role="img" aria-label="QR code placeholder">
      <rect width="100" height="100" fill="#fff" />
      {cells}
      {finder(0, 0)}
      {finder(N - 7, 0)}
      {finder(0, N - 7)}
    </svg>
  );
};
